import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { getDefaultsForVertical } from '@ferro/config';
import {
  DAY_KEYS,
  DAY_LABELS,
  ENTITY_TYPES,
  centsToDollarsInput,
  describeHoursError,
  dollarsInputToCents,
  isValidEinOrEmpty,
  isValidPhone,
  describePhoneError,
  readTenantConfigDraft,
  seedFromVerticalDefaults,
  setAddressField,
  setBusinessMetaField,
  setCurrency,
  setDayEnabled,
  setDayWindow,
  setLocale,
  setServiceActive,
  setServiceDurationMinutes,
  setServicePriceCents,
  setTimezone,
  validateBusinessHoursWindow,
  type BusinessAddressDraft,
  type DayKey,
  type EntityType,
} from '../../../lib/tenant-config-form.js';

/**
 * Step 4 of the tenant provisioning wizard (D1.5.5) — Config intake.
 *
 * Collects:
 *   • Timezone / currency / locale (defaults from vertical)
 *   • Business hours grid (one window per day; day can be closed)
 *   • Service catalog (from vertical defaults, inline-editable)
 *   • Business meta (legal entity + EIN + address + phone)
 *
 * Out of scope (intake portal D2 owns): multi-window days, holiday
 * editor, rich service editor (add/remove), terminology overrides.
 *
 * Defaults are pre-populated on first mount from `getDefaultsForVertical`
 * (D1.3.2). The admin edits inline; validation is inline (close > open,
 * EIN regex if present); Zod parsing runs at commit time (D1.6.1).
 */

type StepConfigProps = {
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
};

export function StepConfig({ payload, updatePayload }: StepConfigProps): JSX.Element {
  const draft = useMemo(() => readTenantConfigDraft(payload), [payload]);
  const vertical =
    typeof payload.vertical === 'string' ? payload.vertical : 'pet-services';
  const defaults = useMemo(() => getDefaultsForVertical(vertical), [vertical]);

  // Seed defaults on first mount; re-seeds are no-ops because
  // seedFromVerticalDefaults only overlays absent fields.
  useEffect(() => {
    if (!draft.serviceCatalog && !draft.businessHours) {
      const next = seedFromVerticalDefaults(draft, defaults);
      updatePayload({ config: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTimezone = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ config: setTimezone(readTenantConfigDraft(payload), e.target.value) });
  };
  const onCurrency = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ config: setCurrency(readTenantConfigDraft(payload), e.target.value) });
  };
  const onLocale = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ config: setLocale(readTenantConfigDraft(payload), e.target.value) });
  };

  const onDayEnabled = (day: DayKey) => (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ config: setDayEnabled(readTenantConfigDraft(payload), day, e.target.checked) });
  };
  const onDayField = (day: DayKey, field: 'open' | 'close') =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      updatePayload({
        config: setDayWindow(readTenantConfigDraft(payload), day, field, e.target.value),
      });
    };

  const onServiceActive = (slug: string) => (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({
      config: setServiceActive(readTenantConfigDraft(payload), slug, e.target.checked),
    });
  };
  const onServicePrice = (slug: string) => (e: ChangeEvent<HTMLInputElement>): void => {
    const cents = dollarsInputToCents(e.target.value);
    if (cents === null) return; // Non-numeric; don't corrupt the draft.
    updatePayload({
      config: setServicePriceCents(readTenantConfigDraft(payload), slug, cents),
    });
  };
  const onServiceDuration = (slug: string) => (e: ChangeEvent<HTMLInputElement>): void => {
    const n = Number(e.target.value);
    if (!Number.isFinite(n) || n < 0) return;
    updatePayload({
      config: setServiceDurationMinutes(
        readTenantConfigDraft(payload),
        slug,
        Math.round(n),
      ),
    });
  };

  const onMetaString = (key: 'legalEntityName' | 'einTaxId' | 'primaryPhone') =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      updatePayload({
        config: setBusinessMetaField(readTenantConfigDraft(payload), key, e.target.value),
      });
    };
  const onEntityType = (e: ChangeEvent<HTMLSelectElement>): void => {
    const next = e.target.value as EntityType;
    updatePayload({
      config: setBusinessMetaField(readTenantConfigDraft(payload), 'entityType', next),
    });
  };
  const onAddress = (key: keyof BusinessAddressDraft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      updatePayload({
        config: setAddressField(readTenantConfigDraft(payload), key, e.target.value),
      });
    };

  const ein = draft.businessMeta?.einTaxId ?? '';
  const einOk = isValidEinOrEmpty(ein);
  const phone = draft.businessMeta?.primaryPhone ?? '';
  const phoneOk = isValidPhone(phone);

  return (
    <div className="ferro-form" aria-label="Config — Step 4">
      <section className="ferro-field" aria-labelledby="step-config-locale-heading">
        <h2 id="step-config-locale-heading" className="ferro-field__label">
          Locale &amp; currency
        </h2>

        <div className="ferro-field">
          <label htmlFor="step-config-timezone" className="ferro-field__label">
            Timezone (IANA)
          </label>
          <input
            id="step-config-timezone"
            type="text"
            name="timezone"
            className="ferro-field__input"
            value={draft.timezone ?? ''}
            onChange={onTimezone}
            placeholder="America/Denver"
            data-testid="step-config-timezone"
          />
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-currency" className="ferro-field__label">
            Currency (ISO 4217)
          </label>
          <input
            id="step-config-currency"
            type="text"
            name="currency"
            className="ferro-field__input"
            value={draft.currency ?? ''}
            onChange={onCurrency}
            placeholder="USD"
            maxLength={3}
            data-testid="step-config-currency"
          />
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-locale" className="ferro-field__label">
            Locale (BCP 47)
          </label>
          <input
            id="step-config-locale"
            type="text"
            name="locale"
            className="ferro-field__input"
            value={draft.locale ?? ''}
            onChange={onLocale}
            placeholder="en-US"
            data-testid="step-config-locale"
          />
        </div>
      </section>

      <section className="ferro-field" aria-labelledby="step-config-hours-heading">
        <h2 id="step-config-hours-heading" className="ferro-field__label">
          Business hours
        </h2>
        <p className="ferro-muted">
          One window per day; closing time must be after opening. Admins can
          add split windows (e.g. lunch closures) in the later intake portal.
        </p>
        <ul className="ferro-wizard__hours">
          {DAY_KEYS.map((day) => {
            const row = draft.businessHours?.[day];
            const enabled = row?.enabled ?? false;
            const open = row?.open ?? '';
            const close = row?.close ?? '';
            const validation = enabled ? validateBusinessHoursWindow(open, close) : null;
            const errorMsg =
              validation && !validation.ok ? describeHoursError(validation.reason) : null;
            return (
              <li key={day} className="ferro-wizard__hours-row" data-testid={`step-config-day-${day}`}>
                <label className="ferro-wizard__hours-day">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={onDayEnabled(day)}
                    data-testid={`step-config-day-toggle-${day}`}
                  />
                  <span>{DAY_LABELS[day]}</span>
                </label>
                <div className="ferro-wizard__hours-inputs">
                  <label>
                    <span className="ferro-muted">Open</span>
                    <input
                      type="time"
                      value={open}
                      disabled={!enabled}
                      onChange={onDayField(day, 'open')}
                      aria-invalid={errorMsg ? true : undefined}
                      data-testid={`step-config-day-open-${day}`}
                    />
                  </label>
                  <label>
                    <span className="ferro-muted">Close</span>
                    <input
                      type="time"
                      value={close}
                      disabled={!enabled}
                      onChange={onDayField(day, 'close')}
                      aria-invalid={errorMsg ? true : undefined}
                      data-testid={`step-config-day-close-${day}`}
                    />
                  </label>
                </div>
                {errorMsg ? (
                  <span
                    role="alert"
                    className="ferro-form__error"
                    data-testid={`step-config-day-error-${day}`}
                  >
                    {errorMsg}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="ferro-field" aria-labelledby="step-config-services-heading">
        <h2 id="step-config-services-heading" className="ferro-field__label">
          Service catalog
        </h2>
        <p className="ferro-muted">
          Pre-populated from the <code>{vertical}</code> vertical. Toggle,
          reprice, or adjust duration. Adding or removing services happens
          in the intake portal.
        </p>
        <ul className="ferro-wizard__services">
          {(draft.serviceCatalog ?? []).map((svc) => {
            return (
              <li
                key={svc.slug}
                className="ferro-wizard__services-row"
                data-testid={`step-config-service-${svc.slug}`}
              >
                <div className="ferro-wizard__services-head">
                  <label className="ferro-wizard__services-active">
                    <input
                      type="checkbox"
                      checked={svc.active}
                      onChange={onServiceActive(svc.slug)}
                      data-testid={`step-config-service-active-${svc.slug}`}
                    />
                    <span>{svc.displayName}</span>
                  </label>
                  <span className="ferro-muted">{svc.slug}</span>
                </div>
                <div className="ferro-wizard__services-fields">
                  <label>
                    <span className="ferro-muted">Price ($)</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={centsToDollarsInput(svc.priceCents)}
                      onChange={onServicePrice(svc.slug)}
                      disabled={!svc.active}
                      data-testid={`step-config-service-price-${svc.slug}`}
                    />
                  </label>
                  <label>
                    <span className="ferro-muted">Duration (min)</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={svc.durationMinutes || ''}
                      onChange={onServiceDuration(svc.slug)}
                      disabled={!svc.active}
                      data-testid={`step-config-service-duration-${svc.slug}`}
                    />
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="ferro-field" aria-labelledby="step-config-meta-heading">
        <h2 id="step-config-meta-heading" className="ferro-field__label">
          Business meta
        </h2>

        <div className="ferro-field">
          <label htmlFor="step-config-legal-name" className="ferro-field__label">
            Legal entity name
          </label>
          <input
            id="step-config-legal-name"
            type="text"
            className="ferro-field__input"
            value={draft.businessMeta?.legalEntityName ?? ''}
            onChange={onMetaString('legalEntityName')}
            placeholder="Happy Tail Happy Dog LLC"
            data-testid="step-config-legal-name"
          />
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-entity-type" className="ferro-field__label">
            Entity type
          </label>
          <select
            id="step-config-entity-type"
            className="ferro-field__input"
            value={draft.businessMeta?.entityType ?? ''}
            onChange={onEntityType}
            data-testid="step-config-entity-type"
          >
            <option value="" disabled>
              Select entity type
            </option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-ein" className="ferro-field__label">
            EIN / Tax ID (optional)
          </label>
          <input
            id="step-config-ein"
            type="text"
            className="ferro-field__input"
            value={ein}
            onChange={onMetaString('einTaxId')}
            placeholder="12-3456789"
            aria-invalid={!einOk ? true : undefined}
            aria-describedby={!einOk ? 'step-config-ein-error' : undefined}
            data-testid="step-config-ein"
          />
          {!einOk ? (
            <span
              id="step-config-ein-error"
              role="alert"
              className="ferro-form__error"
              data-testid="step-config-ein-error"
            >
              EIN must be formatted NN-NNNNNNN.
            </span>
          ) : null}
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-phone" className="ferro-field__label">
            Primary phone <span className="ferro-field__required">*</span>
          </label>
          <input
            id="step-config-phone"
            type="tel"
            className="ferro-field__input"
            value={phone}
            onChange={onMetaString('primaryPhone')}
            placeholder="+1 303 555 0100"
            aria-invalid={!phoneOk ? true : undefined}
            aria-describedby={!phoneOk ? 'step-config-phone-error' : undefined}
            data-testid="step-config-phone"
          />
          {!phoneOk ? (
            <span
              id="step-config-phone-error"
              role="alert"
              className="ferro-form__error"
              data-testid="step-config-phone-error"
            >
              {describePhoneError(phone)}
            </span>
          ) : null}
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-addr-line1" className="ferro-field__label">
            Address line 1
          </label>
          <input
            id="step-config-addr-line1"
            type="text"
            className="ferro-field__input"
            value={draft.businessMeta?.primaryAddress?.line1 ?? ''}
            onChange={onAddress('line1')}
            placeholder="123 Main St"
            data-testid="step-config-addr-line1"
          />
        </div>

        <div className="ferro-field">
          <label htmlFor="step-config-addr-line2" className="ferro-field__label">
            Address line 2 (optional)
          </label>
          <input
            id="step-config-addr-line2"
            type="text"
            className="ferro-field__input"
            value={draft.businessMeta?.primaryAddress?.line2 ?? ''}
            onChange={onAddress('line2')}
            placeholder="Suite 200"
            data-testid="step-config-addr-line2"
          />
        </div>

        <div className="ferro-wizard__addr-row">
          <div className="ferro-field">
            <label htmlFor="step-config-addr-city" className="ferro-field__label">
              City
            </label>
            <input
              id="step-config-addr-city"
              type="text"
              className="ferro-field__input"
              value={draft.businessMeta?.primaryAddress?.city ?? ''}
              onChange={onAddress('city')}
              placeholder="Denver"
              data-testid="step-config-addr-city"
            />
          </div>
          <div className="ferro-field">
            <label htmlFor="step-config-addr-state" className="ferro-field__label">
              State
            </label>
            <input
              id="step-config-addr-state"
              type="text"
              className="ferro-field__input"
              value={draft.businessMeta?.primaryAddress?.state ?? ''}
              onChange={onAddress('state')}
              placeholder="CO"
              maxLength={2}
              data-testid="step-config-addr-state"
            />
          </div>
          <div className="ferro-field">
            <label htmlFor="step-config-addr-postal" className="ferro-field__label">
              Postal code
            </label>
            <input
              id="step-config-addr-postal"
              type="text"
              className="ferro-field__input"
              value={draft.businessMeta?.primaryAddress?.postalCode ?? ''}
              onChange={onAddress('postalCode')}
              placeholder="80203"
              data-testid="step-config-addr-postal"
            />
          </div>
          <div className="ferro-field">
            <label htmlFor="step-config-addr-country" className="ferro-field__label">
              Country
            </label>
            <input
              id="step-config-addr-country"
              type="text"
              className="ferro-field__input"
              value={draft.businessMeta?.primaryAddress?.country ?? ''}
              onChange={onAddress('country')}
              placeholder="US"
              maxLength={2}
              data-testid="step-config-addr-country"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
